import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from 'react';
import { supabase, getCurrentUser } from './supabase';
import { User } from '@supabase/supabase-js';

// Define the shape of our auth context
interface AuthContextType {
  user: User | null;
  profile: any | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

// Create the auth context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  isLoading: true,
  isAuthenticated: false,
  signOut: async () => {},
  refreshUser: async () => {},
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Memoize the refreshUser function to prevent recreation
  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Error refreshing user:', error);
      setUser(null);
    }
  }, []);

  // Memoize the signOut function to prevent recreation
  const handleSignOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      // Batch state updates to prevent multiple re-renders
      setUser(null);
      setProfile(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, []);

  // Effect to load the user on mount and set up auth state change listener
  useEffect(() => {
    let isMounted = true;

    // Initial user fetch
    const fetchUser = async () => {
      if (!isMounted) return;
      
      setIsLoading(true);
      try {
        await refreshUser();
      } catch (error) {
        console.error('Error fetching user:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchUser();

    // Set up auth state change listener with optimized state updates
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (!isMounted) return;

        // Batch state updates to prevent multiple re-renders
        if (session?.user) {
          setUser(session.user);
          setIsLoading(false);
        } else {
          // Use functional updates to batch state changes
          setUser(null);
          setProfile(null);
          setIsLoading(false);
        }
      }
    );

    // Clean up the subscription when the component unmounts
    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [refreshUser]);

  // Optimized profile fetch with debouncing
  useEffect(() => {
    let isMounted = true;
    
    const fetchProfile = async () => {
      if (!isMounted) return;
      
      if (!user) {
        setProfile(null);
        return;
      }
      
      try {
        const ADMIN_USER_ID = '93794bbb-34c2-48f4-a292-1f55434bcdba';
        const isAdmin = user.id === ADMIN_USER_ID;
        
        const newProfile = {
          id: user.id,
          email: user.email || '',
          full_name: user.user_metadata?.full_name || user.email || '',
          is_admin: isAdmin
        };

        if (isMounted) {
          setProfile(newProfile);
        }
      } catch (error) {
        console.error('Error creating basic profile:', error);
        if (isMounted) {
          setProfile(null);
        }
      }
    };
    
    fetchProfile();

    return () => {
      isMounted = false;
    };
  }, [user]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    profile,
    isLoading,
    isAuthenticated: !!user,
    signOut: handleSignOut,
    refreshUser,
  }), [user, profile, isLoading, handleSignOut, refreshUser]);

  // Provide the auth context to children components
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
